import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Sidebar";
import { API_BASE_URL } from "../../config/api";

const pagesList = ["dashboard", "products", "parts", "orders", "analysis"];



const AdminSettings = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("authToken");

  const isAdmin = user?.role === "admin";

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState([]);

  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    role: "user",
  });

  const [editForm, setEditForm] = useState({
    username: "",
    password: "",
  });

  // ================= FETCH USERS =================
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch users");

      const data = await res.json();
      setUsers(data || []);
    } catch (err) {
      console.error("Fetch users error:", err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ================= CREATE USER =================
  const createUser = async () => {
    if (!createForm.username || !createForm.password) {
      return alert("Username & password required");
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(createForm),
      });

      if (!res.ok) throw new Error("Failed to create user");

      setCreateForm({ username: "", password: "", role: "user" });
      fetchUsers();
    } catch (err) {
      console.error("Create user error:", err);
    }
  };

  // ================= DELETE USER =================
  const deleteUser = async (id) => {
    if (!window.confirm("Delete user?")) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Delete failed");

      fetchUsers();
      if (selectedUser?.id === id) setSelectedUser(null);
    } catch (err) {
      console.error("Delete user error:", err);
    }
  };

  // ================= OPEN USER =================
  const openUser = async (u) => {
    setSelectedUser(u);
    setEditForm({ username: u.username, password: "" });

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/users/${u.id}/permissions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) throw new Error("Permissions fetch failed");

      const perms = await res.json();
      setPermissions(perms || []);
    } catch (err) {
      console.error("Permissions error:", err);
      setPermissions([]);
    }
  };

  // ================= SAVE USER =================
  const saveUser = async () => {
    if (!selectedUser) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/users/${selectedUser.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editForm),
        }
      );

      if (!res.ok) throw new Error("Save failed");

      fetchUsers();
    } catch (err) {
      console.error("Save user error:", err);
    }
  };

  // ================= PERMISSIONS =================
  const togglePage = (page) => {
    setPermissions((prev) =>
      prev.includes(page)
        ? prev.filter((p) => p !== page)
        : [...prev, page]
    );
  };

  const savePermissions = async () => {
    if (!selectedUser) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/users/${selectedUser.id}/permissions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ pages: permissions }),
        }
      );

      if (!res.ok) throw new Error("Save permissions failed");

      alert("Permissions updated");
      fetchUsers();
    } catch (err) {
      console.error("Save permissions error:", err);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />

      <main className="flex-1 p-10 ml-64">
        {/* HEADER */}
        <div className="flex justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Admin Settings</h1>
            <p className="text-gray-500">
              Logged in as{" "}
              <span className="text-blue-600 font-semibold">
                {user?.username}
              </span>
            </p>
          </div>
        </div>

        {isAdmin && (
          <div className="space-y-6">
            {/* CREATE USER */}
            <div className="bg-white p-6 rounded-2xl shadow">
              <h2 className="font-bold mb-4">Create User</h2>

              <input
                placeholder="Username"
                value={createForm.username}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    username: e.target.value,
                  })
                }
                className="border p-2 w-full mb-2"
              />

              <input
                type="password"
                placeholder="Password"
                value={createForm.password}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    password: e.target.value,
                  })
                }
                className="border p-2 w-full mb-2"
              />

              <select
                value={createForm.role}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    role: e.target.value,
                  })
                }
                className="border p-2 w-full mb-2"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>

              <button
                onClick={createUser}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Create
              </button>
            </div>

            {/* USERS LIST */}
            <div className="bg-white p-6 rounded-2xl shadow">
              <h2 className="font-bold mb-4">Users</h2>

              {users.length === 0 ? (
                <p className="text-gray-500">No users found</p>
              ) : (
                users.map((u) => (
                  <div
                    key={u.id}
                    className="flex justify-between p-3 border mb-2 rounded"
                  >
                    <div>
                      <div className="font-semibold">{u.username}</div>
                      <div className="text-sm text-gray-500">{u.role}</div>
                    </div>

                    <div className="flex gap-2">
                      <button onClick={() => openUser(u)}>Edit</button>
                      <button onClick={() => deleteUser(u.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* EDIT USER */}
            {selectedUser && (
              <div className="bg-white p-6 rounded-2xl shadow">
                <h2 className="font-bold mb-4">
                  Edit {selectedUser.username}
                </h2>

                <input
                  value={editForm.username}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      username: e.target.value,
                    })
                  }
                  className="border p-2 w-full mb-2"
                />

                <input
                  type="password"
                  placeholder="New Password"
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      password: e.target.value,
                    })
                  }
                  className="border p-2 w-full mb-4"
                />

                <button
                  onClick={saveUser}
                  className="bg-green-600 text-white px-4 py-2 rounded mb-4"
                >
                  Save User
                </button>

                <h3 className="font-semibold mb-2">Permissions</h3>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  {pagesList.map((p) => (
                    <label key={p} className="flex gap-2">
                      <input
                        type="checkbox"
                        checked={permissions.includes(p)}
                        onChange={() => togglePage(p)}
                      />
                      {p}
                    </label>
                  ))}
                </div>

                <button
                  onClick={savePermissions}
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  Save Permissions
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSettings;